import { startTransition, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import T, { useTranslation } from '@wojtekmaj/react-t';

import { toast } from '@rewardopl/react-toast';

import Button from '@rewardopl/react-ui/button';
import CheckboxField from '@rewardopl/react-ui/checkbox_field';
import InputField from '@rewardopl/react-ui/input_field';

import { patch } from '@rewardopl/utils/network';

import {
  ButtonWrapper,
  CheckboxWrapper,
  ContentWrapper,
  Footer,
  Heading,
  ScrollContainer,
  Wrapper,
} from './index.styles';

import MarketingAndAgeControls from '../../../../../common/marketing_and_age_controls';
import MarketingDisclaimers from '../../../../../common/marketing_disclaimers';
import PrivacyPolicyLink from '../../../../../privacy_policy_link';
import TermsAndConditionsLink from '../../../../../terms_and_conditions_link';

import { currentUserState } from '../../../../../../store';

import type { User } from '@rewardopl/types';

export default function AppConsentsPage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useAtom(currentUserState);
  const acceptTermsErrorString = useTranslation('Failed to accept Terms and Conditions');

  const { email: userEmail, mobile: userMobile, terms_accepted: userTermsAccepted } = currentUser;

  const [tandcAccepted, setTandcAccepted] = useState(false);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [is16OrOlder, setIs16OrOlder] = useState<boolean | null>(null);
  const [marketingEmailAccepted, setMarketingEmailAccepted] = useState(false);
  const [marketingSmsAccepted, setMarketingSmsAccepted] = useState(false);
  const [marketingProfilingAccepted, setMarketingProfilingAccepted] = useState(false);

  const termsAccepted = tandcAccepted && privacyPolicyAccepted;
  const marketingAccepted =
    marketingEmailAccepted || marketingSmsAccepted || marketingProfilingAccepted;

  if (userTermsAccepted) {
    return <Navigate to=".." />;
  }

  const action = '/api/users/current';

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const form = event.currentTarget;

    if (!form || !form.reportValidity()) {
      return;
    }

    const formData = new FormData(form);
    const filteredFormData = new FormData();

    for (const [key, value] of formData.entries()) {
      if (value !== '') {
        filteredFormData.append(key, value);
      }
    }

    formData.append('terms_accepted', termsAccepted.toString());
    if (is16OrOlder !== null) {
      formData.append('marketing_accepted', marketingAccepted.toString());
      formData.append('marketing_email_accepted', marketingEmailAccepted.toString());
      formData.append('marketing_sms_accepted', marketingSmsAccepted.toString());
      formData.append('marketing_profiling_accepted', marketingProfilingAccepted.toString());
    }

    try {
      const user = (await patch(action, {
        body: formData,
      })) as User;

      startTransition(() => {
        setCurrentUser(user);

        navigate('../');
      });
    } catch {
      toast.error(acceptTermsErrorString);
    }
  }

  return (
    <form action={action} method="PATCH" onSubmit={onSubmit}>
      <Wrapper>
        <ScrollContainer>
          <ContentWrapper>
            <Heading>
              <T>Update your info</T>
            </Heading>
            <CheckboxWrapper>
              {userMobile ? null : (
                <InputField
                  autoComplete="tel"
                  label="Mobile"
                  name="mobile"
                  required={marketingSmsAccepted}
                  type="tel"
                />
              )}
              {userEmail ? null : (
                <InputField
                  autoComplete="username"
                  label="Email"
                  maxLength={320}
                  name="email"
                  placeholder="email@example.com"
                  required={marketingEmailAccepted}
                  type="email"
                />
              )}
              <MarketingAndAgeControls
                form="_private"
                is16OrOlder={is16OrOlder}
                marketingEmailAccepted={marketingEmailAccepted}
                marketingProfilingAccepted={marketingProfilingAccepted}
                marketingSmsAccepted={marketingSmsAccepted}
                setIs16OrOlder={setIs16OrOlder}
                setMarketingEmailAccepted={setMarketingEmailAccepted}
                setMarketingProfilingAccepted={setMarketingProfilingAccepted}
                setMarketingSmsAccepted={setMarketingSmsAccepted}
              />
              <MarketingDisclaimers />
              <CheckboxField
                checked={tandcAccepted}
                label={
                  <>
                    <T>I have read and agree to the</T> <TermsAndConditionsLink />.
                  </>
                }
                name="tandc_accepted"
                onChange={() => setTandcAccepted(!tandcAccepted)}
                required
              />
              <CheckboxField
                checked={privacyPolicyAccepted}
                label={
                  <>
                    <T>I have read and agree to the</T> <PrivacyPolicyLink />.
                  </>
                }
                name="privacy_policy_accepted"
                onChange={() => setPrivacyPolicyAccepted(!privacyPolicyAccepted)}
                required
              />
            </CheckboxWrapper>
          </ContentWrapper>
        </ScrollContainer>
        <Footer>
          <ButtonWrapper>
            <Button className="primary" type="submit">
              <T>Continue</T>
            </Button>
          </ButtonWrapper>
        </Footer>
      </Wrapper>
    </form>
  );
}
